import { HttpClient, HttpHeaders, HttpResponse } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { catchError, filter, finalize, map, take } from 'rxjs/operators';
import { BANNER_TYPE } from 'src/app/components/banner/banner.constants';
import { environment } from 'src/environments/environment';

import {
  AddOnDemandPickupRequest,
  AddOnDemandPickupResponse,
  EditOnDemandPickupRequest,
  EditOnDemandPickupResponse,
  OnDemandPickupResponse,
} from '../../../models/operations/operations-on-demand-pickup.model';

import {
  AssignReassignRequest,
  AssignReassignResponse,
  OperationsPickupResponse,
  PickupAssignmentsResponse,
  SaveScheduledPickupRequest,
  SaveScheduledPickupResponse,
  ValidateAddressRequest,
  ValidateAddressResponse,
} from '../../../models/operations/operations-pickup.model';

import { ScheduledPickupResponse } from '../../../models/operations/operations-scheduled-pickup.model';
import { CommonUtils } from '../../../utils/common/common.utils';
import { ErrorhandleService } from '../../error-handling/error-handle/errorhandle.service';

@Injectable({
  providedIn: 'root',
})
export class PickupAssignmentsService {
  baseUrl = environment.apiUrl.concat(
    'operations-management/pickup-assignments/',
  );

  constructor(
    private http: HttpClient,
    private commonUtils: CommonUtils,
    private errorHandler: ErrorhandleService,
  ) {}

  getOnDemandPickups(
    locationCode: string,
    orderNumber: string,
  ): Observable<OnDemandPickupResponse> {
    this.commonUtils.updateLoadingModal(true);
    return this.http
      .get<OnDemandPickupResponse>(this.baseUrl.concat('get-ondemand-pickup'), {
        params: { locationCode: locationCode, orderNumber: orderNumber },
      })
      .pipe(
        take(1),
        catchError((error) =>
          this.errorHandler.handleError<OnDemandPickupResponse>(
            error,
            error.status,
            'Operations Service: Get On Demand Pickups',
          ),
        ),
        finalize(() => {
          this.commonUtils.updateLoadingModal(false);
        }),
      );
  }

  getAllPickups(
    date: string,
    locationCode: string,
  ): Observable<OperationsPickupResponse> {
    this.commonUtils.updateLoadingModal(true);
    this.commonUtils.lockUpdateLoadingModal(true);
    return this.http
      .get<PickupAssignmentsResponse>(this.baseUrl.concat('get-all-pickups'), {
        params: { locationcode: locationCode, date: date },
      })
      .pipe(
        take(1),
        map((value) => {
          return {
            response: {
              pickupAssignments: value.response['Pickup Assignments'],
            },
          };
        }),
        catchError((error) =>
          this.errorHandler.handleError<OperationsPickupResponse>(
            error,
            error.status,
            'Operation Service: Get All Pickups',
            BANNER_TYPE.TABLE_DATA_ERROR,
          ),
        ),
        finalize(() => {
          this.commonUtils.lockUpdateLoadingModal(false);
          this.commonUtils.updateLoadingModal(false);
        }),
      );
  }

  editOnDemandPickup(
    onDemandPickup: EditOnDemandPickupRequest,
    locationCode: string,
  ): Observable<EditOnDemandPickupResponse> {
    this.commonUtils.updateLoadingModal(true);
    return this.http
      .put<EditOnDemandPickupResponse>(
        this.baseUrl.concat('edit-ondemand-pickup'),
        onDemandPickup,
        { headers: { locationCode: locationCode } },
      )
      .pipe(
        take(1),
        catchError((error) =>
          this.errorHandler.handleError<EditOnDemandPickupResponse>(
            error,
            error.status,
            'Operation Service: Edit On Demand Pickup',
          ),
        ),
        finalize(() => {
          this.commonUtils.updateLoadingModal(false);
        }),
      );
  }

  getScheduledPickups(
    locationcode: string,
    shippernumber: string,
    id: number,
  ): Observable<ScheduledPickupResponse> {
    this.commonUtils.updateLoadingModal(true);
    return this.http
      .get<ScheduledPickupResponse>(
        this.baseUrl.concat('get-schedule-pickup'),
        {
          params: {
            locationcode: locationcode,
            shippernumber: shippernumber,
            id: id,
          },
        },
      )
      .pipe(
        take(1),
        catchError((error) =>
          this.errorHandler.handleError<ScheduledPickupResponse>(
            error,
            error.status,
            'Operations Service: Get Scheduled Pickups',
          ),
        ),
        finalize(() => {
          this.commonUtils.updateLoadingModal(false);
        }),
      );
  }

  saveScheduledPickup(
    scheduledPickup: SaveScheduledPickupRequest,
    locationCode: string,
  ): Observable<SaveScheduledPickupResponse> {
    this.commonUtils.updateLoadingModal(true);
    return this.http
      .put<SaveScheduledPickupResponse>(
        this.baseUrl.concat('save-schedule-pickup'),
        scheduledPickup,
        {
          params: { locationCode: locationCode },
        },
      )
      .pipe(
        take(1),
        catchError((error) =>
          this.errorHandler.handleError<SaveScheduledPickupResponse>(
            error,
            error.status,
            'Operation Service: Save Scheduled Pickup',
          ),
        ),
        finalize(() => {
          this.commonUtils.updateLoadingModal(false);
        }),
      );
  }

  assignReassign(
    assignReassign: AssignReassignRequest,
    locationCode: string,
  ): Observable<AssignReassignResponse> {
    this.commonUtils.updateLoadingModal(true);
    return this.http
      .put<AssignReassignResponse>(
        this.baseUrl.concat('assign-reassign'),
        assignReassign,
        { headers: { locationCode: locationCode } },
      )
      .pipe(
        take(1),
        catchError((error) =>
          this.errorHandler.handleError<AssignReassignResponse>(
            error,
            error.status,
            'Operation Service: Assign Re-assign',
          ),
        ),
        finalize(() => {
          this.commonUtils.updateLoadingModal(false);
        }),
      );
  }

  validateAddress(
    body: ValidateAddressRequest | any,
    locationCode: string,
  ): Observable<ValidateAddressResponse> {
    this.commonUtils.updateLoadingModal(true);

    const url = this.baseUrl.concat('validate-address');
    const headers = this.buildHeaders(locationCode);
    const normalizedBody = this.normalizeAddressPayload(body);
    const payload = { addressValidation: normalizedBody };

    return this.http
      .post<ValidateAddressResponse>(url, payload, {
        headers,
        observe: 'response',
      })
      .pipe(
        take(1),
        map((resp: HttpResponse<ValidateAddressResponse>) => {
          return resp.body as ValidateAddressResponse;
        }),
        catchError((error) =>
          this.errorHandler.handleError<ValidateAddressResponse>(
            error,
            error.status,
            'Operations Service: Validate Address',
            BANNER_TYPE.API_ERROR,
            false as unknown as ValidateAddressResponse,
          ),
        ),
        filter((value) => !!value),
        finalize(() => {
          this.commonUtils.updateLoadingModal(false);
        }),
      );
  }

  addOnDemandPickup(
    body: AddOnDemandPickupRequest,
    locationCode: string,
  ): Observable<AddOnDemandPickupResponse> {
    this.commonUtils.updateLoadingModal(true);

    const url = this.baseUrl.concat('addondemandpickup');
    const headers = this.buildHeaders(locationCode);

    return this.http
      .post<AddOnDemandPickupResponse>(url, body, {
        headers,
        observe: 'response',
      })
      .pipe(
        take(1),
        map((resp: HttpResponse<AddOnDemandPickupResponse>) => {
          return resp.body as AddOnDemandPickupResponse;
        }),
        catchError((error) =>
          this.errorHandler.handleError<AddOnDemandPickupResponse>(
            error,
            error.status,
            'Operations Service: Add OnDemand Pickup',
            BANNER_TYPE.API_ERROR,
            false as unknown as AddOnDemandPickupResponse,
          ),
        ),
        filter((value) => !!value),
        finalize(() => {
          this.commonUtils.updateLoadingModal(false);
        }),
      );
  }

  /** Include both likely header names + JSON content type */
  private buildHeaders(locationCode: string): HttpHeaders {
    return new HttpHeaders({
      locationCode: locationCode,
      location: locationCode,
      'Content-Type': 'application/json',
    });
  }

  /**
   * Normalize payload
   */
  private normalizeAddressPayload(input: any): {
    address: Record<string, any>;
    orderNumber?: string;
    userId?: string;
  } {
    const root = input?.addressValidation ? input.addressValidation : input;
    const rawAddress = root?.address ?? {};
    const sp = rawAddress?.streetParsed ?? {};

    const streetLine1 = [sp.number, sp.prefix, sp.name, sp.type, sp.suffix]
      .filter(Boolean)
      .join(' ')
      .trim();

    const cleanedAddress: Record<string, any> = {
      consignee: rawAddress?.consignee,
      country: rawAddress?.country,
      city: rawAddress?.city,
      postalCode: rawAddress?.postalCode,
      streetParsed: rawAddress?.streetParsed,
    };

    if (
      rawAddress?.country &&
      String(rawAddress.country).toUpperCase() !== 'IE'
    ) {
      if (rawAddress?.state) cleanedAddress.state = rawAddress.state;
    }

    this.pruneEmpty(cleanedAddress);

    const result = {
      address: cleanedAddress,
      orderNumber: root?.orderNumber,
      userId: root?.userId,
    };

    this.pruneEmpty(result);
    return result;
  }

  /** Remove keys with null/undefined/empty-string values */
  private pruneEmpty(obj: Record<string, any>) {
    Object.keys(obj).forEach((k) => {
      const v = obj[k];
      if (
        v === null ||
        v === undefined ||
        (typeof v === 'string' && v.trim() === '')
      ) {
        delete obj[k];
      }
    });
  }
}
